/**
 * Activities API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { Activity, CreateActivityDto, UpdateActivityDto } from '@/types/activity.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /activities` — paginated activities. */
export const getActivities = async (params?: {
  page?: number;
  pageSize?: number;
  statuses?: string[];
  activityTypeIds?: string[];
  hasChat?: boolean;
  hasBot?: boolean;
  hasLink?: boolean;
  canHaveCr?: boolean;
  search?: string;
}): Promise<PaginatedResponse<Activity>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  params?.activityTypeIds?.forEach((id) => searchParams.append('activityTypeIds', id));
  if (params?.hasChat !== undefined) searchParams.append('hasChat', params.hasChat.toString());
  if (params?.hasBot !== undefined) searchParams.append('hasBot', params.hasBot.toString());
  if (params?.hasLink !== undefined) searchParams.append('hasLink', params.hasLink.toString());
  if (params?.canHaveCr !== undefined)
    searchParams.append('canHaveCr', params.canHaveCr.toString());
  if (params?.search) searchParams.append('search', params.search);
  const { data } = await apiClient.get<PaginatedResponse<Activity>>(
    listUrl('/activities', searchParams),
  );
  return data;
};

/** `GET /activities/:id` */
export const getActivity = async (id: string): Promise<Activity> => {
  const { data } = await apiClient.get(`/activities/${id}`);
  return data;
};

/** `POST /activities` */
export const createActivity = async (dto: CreateActivityDto): Promise<Activity> => {
  const { data } = await apiClient.post('/activities', dto);
  return data;
};

/** `PATCH /activities/:id` */
export const updateActivity = async (id: string, dto: UpdateActivityDto): Promise<Activity> => {
  const { data } = await apiClient.patch(`/activities/${id}`, dto);
  return data;
};

/** `DELETE /activities/:id` */
export const deleteActivity = async (id: string): Promise<void> => {
  await apiClient.delete(`/activities/${id}`);
};

/** `GET /activities/export` — download XLSX. */
export const exportActivitiesExcel = async (): Promise<void> => {
  const response = await apiClient.get('/activities/export', { responseType: 'blob' });
  downloadXlsx(response.data, `activities-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
